import { fs } from '@/theme/tokens';
import { Alert, FormControl, FormHelperText, FormLabel, LinearProgress, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useUploadLimits, type FileRef, type UploadModule } from '@/api/files';
import { openFile, refusal, uploadErrorMessage, uploadFile } from '@/lib/storage-upload';
import type { FieldComponentProps, FieldTypeSpec } from '../types';
import { FileDropZone } from './FileDropZone';
import { FileList, type FileListItem } from './FileList';
import { RecentlyRemoved } from './RecentlyRemoved';

const MB = 1024 * 1024;

/** Same names as the Form.io component's settings, so that component passes them straight through. */
export interface AttachmentConfig {
  module: UploadModule;
  target?: { table: string; field: string };
  /** The saved record the field belongs to: with `target`, the field lists its recently removed files. */
  recordId?: string;
  multipleFiles?: boolean;
  maxFiles?: number;
  maxSizeMb?: number;
  allowedTypes?: string[];
}

interface Pending {
  key: string;
  name: string;
  size: number;
  progress: number | null;
  error?: string;
}

const open = (id: string) => () => { openFile(id).catch(() => undefined); };

/** The field's own restrictions in words; the workspace limits are added once the field can upload. */
function describe(cfg: AttachmentConfig): string {
  return [
    cfg.allowedTypes?.length ? cfg.allowedTypes.join(', ').toUpperCase() : '',
    cfg.maxSizeMb ? `up to ${cfg.maxSizeMb} MB` : '',
    cfg.multipleFiles && cfg.maxFiles ? `max ${cfg.maxFiles} files` : '',
  ].filter(Boolean).join(' · ');
}

/** Upload through app.storage the moment a file is picked; the value is the list of stored files. */
export function AttachmentInput({ field, value, onChange, error }: FieldComponentProps<FileRef[] | null, AttachmentConfig>) {
  const cfg = field.config;
  const editable = !field.disabled && !field.readOnly;
  const limitsQuery = useUploadLimits(cfg.module, editable);
  const refs = value ?? [];
  const latest = useRef(refs);
  latest.current = refs;
  // Uploads finishing together each add to the list; keep it current before the host re-renders, or one
  // would overwrite the other.
  const emit = (next: FileRef[]) => {
    latest.current = next;
    onChange(next);
  };
  // Removing a file only takes it off the form: nothing is deleted until the record saves, and an
  // upload that is never saved is cleaned up after the orphan period.
  const [pending, setPending] = useState<Pending[]>([]);
  const [notice, setNotice] = useState<string>();

  const server = limitsQuery.data;
  const wanted = cfg.allowedTypes?.map((t) => t.toLowerCase().replace(/^\./, ''));
  const limits = server && {
    ...server,
    allowed_extensions: wanted?.length ? server.allowed_extensions.filter((e) => wanted.includes(e)) : server.allowed_extensions,
  };
  const maxBytes = Math.min(cfg.maxSizeMb ? cfg.maxSizeMb * MB : Infinity, server?.max_file_bytes ?? Infinity);
  const maxFiles = cfg.multipleFiles ? Math.min(cfg.maxFiles ?? Infinity, server?.max_files_per_record ?? Infinity) : 1;
  const busy = pending.filter((p) => !p.error).length;

  const update = (key: string, patch: Partial<Pending>) =>
    setPending((list) => list.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const start = (files: File[]) => {
    if (!limits) return;
    const room = cfg.multipleFiles ? maxFiles - refs.length - busy : 1;
    setNotice(files.length > room ? `Only ${maxFiles} file${maxFiles === 1 ? '' : 's'} can be attached here.` : undefined);
    files.slice(0, Math.max(room, 0)).forEach((file) => {
      const key = `${file.name}:${file.size}:${Date.now()}:${Math.random()}`;
      const problem = refusal(file, limits, maxBytes);
      setPending((list) => [...list, { key, name: file.name, size: file.size, progress: 0, error: problem }]);
      if (problem) return;
      uploadFile(file, { module: cfg.module, target: cfg.target, limits, maxBytes,
                         onProgress: (progress) => update(key, { progress }) })
        .then((ref) => {
          setPending((list) => list.filter((p) => p.key !== key));
          emit(cfg.multipleFiles ? [...latest.current, ref] : [ref]);
        })
        .catch((e) => update(key, { error: uploadErrorMessage(e), progress: null }));
    });
  };

  const items: FileListItem[] = [
    ...refs.map((r) => ({
      key: r.id, name: r.missing ? 'File no longer available' : r.name, size: r.size, unavailable: r.missing,
      onOpen: r.missing ? undefined : open(r.id),
      onRemove: editable ? () => emit(latest.current.filter((x) => x.id !== r.id)) : undefined,
    })),
    ...pending.map((p) => ({
      key: p.key, name: p.name, size: p.size, progress: p.progress, uploading: !p.error, error: p.error,
      onRemove: p.error ? () => setPending((list) => list.filter((x) => x.key !== p.key)) : undefined,
    })),
  ];
  const canAdd = cfg.multipleFiles ? refs.length + busy < maxFiles : busy === 0;
  const hint = limits
    ? `${limits.allowed_extensions.join(', ').toUpperCase()} · up to ${Math.max(1, Math.round(maxBytes / MB))} MB`
      + (cfg.multipleFiles && Number.isFinite(maxFiles) ? ` · max ${maxFiles} files` : '')
    : undefined;

  return (
    <FormControl fullWidth={field.fullWidth} error={!!error} required={field.required} className={field.className}
                 sx={field.sx}>
      {field.label && <FormLabel sx={{ fontSize: fs.small, mb: 0.5 }}>{field.label}</FormLabel>}
      {editable && limitsQuery.isLoading && <LinearProgress />}
      {editable && server && !server.can_upload && <Alert severity="info">{server.reason}</Alert>}
      {editable && limits?.can_upload && canAdd && (
        <FileDropZone onFiles={start} multiple={cfg.multipleFiles} hint={hint}
                      accept={limits.allowed_extensions.map((e) => `.${e}`).join(',')}
                      buttonLabel={!cfg.multipleFiles && refs.length ? 'Replace file' : undefined} {...field.props} />
      )}
      {/* A disabled field (a form designer's canvas, a locked step) still shows what it will accept. */}
      {field.disabled && !field.readOnly && (
        <FileDropZone onFiles={() => undefined} multiple={cfg.multipleFiles} disabled
                      hint={describe(cfg) || 'Files are added when the form is filled in.'} {...field.props} />
      )}
      {field.readOnly && !refs.length && <Typography variant="body2" color="text.disabled">No files</Typography>}
      <FileList items={items} disabled={!editable} />
      {/* A restore saves the record on the server; adding the file here too keeps the next save from dropping it. */}
      {editable && cfg.target && cfg.recordId && (
        <RecentlyRemoved refTable={cfg.target.table} refId={cfg.recordId} field={cfg.target.field}
                         onRestored={(ref) => emit(cfg.multipleFiles ? [...latest.current, ref] : [ref])} />
      )}
      {notice && <Typography variant="caption" color="warning.main">{notice}</Typography>}
      {(error ?? field.helperText) && <FormHelperText>{error ?? field.helperText}</FormHelperText>}
    </FormControl>
  );
}

/** Read-only links to stored files, for lists and detail views. */
export function FileRefLinks({ refs }: { refs: FileRef[] }) {
  return (
    <FileList items={refs.map((r) => r.missing
      ? { key: r.id, name: 'File no longer available', size: 0, unavailable: true }
      : { key: r.id, name: r.name, size: r.size, onOpen: open(r.id) })} />
  );
}

export const attachmentTypes: Record<string, FieldTypeSpec> = {
  attachment: {
    component: AttachmentInput,
    defaults: { grid: { xs: 12, md: 12 } },
    display: (value) => <FileRefLinks refs={value as FileRef[]} />,
  },
};
